import useFormatter from "@/hooks/use-formatter.hook.ts";
import useGoalsHook from "@/hooks/use-goals.hook.tsx";
import { FlatGoalRow } from "@/pages/talent/goals/hooks/use-goals-table.hook.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiGoal } from "@/types/goals.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  LinkTo,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import {
  HTMLAttributeAnchorTarget,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function TruncatedGoalTitle({ title }: { title: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [title]);

  const textElement = (
    <span ref={textRef} className={"text-dark/95 font-semibold line-clamp-2 min-w-40"}>
      {title}
    </span>
  );

  if (isTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent className="max-w-80">{title}</TooltipContent>
      </Tooltip>
    );
  }

  return textElement;
}

export interface GoalsTableProps {
  flatGoals: FlatGoalRow[];
  isLoading: boolean;
  expandedPaths: Set<string>;
  loadingPaths: Set<string>;
  toggleExpand: (goalId: number, path: string) => Promise<void>;
  handleEditGoal: (goal: ApiGoal) => void;
  handleDetailsGoal: (goal: ApiGoal) => void;
  handleDeleteGoal: (goal: ApiGoal) => void;
  emptyStateComponent?: ReactNode;
  onItemClick?: (goal: ApiGoal) => void;
  targetLink?: HTMLAttributeAnchorTarget;
  stickyHeader?: boolean;
}

export function GoalsTable({
  flatGoals,
  isLoading,
  expandedPaths,
  loadingPaths,
  toggleExpand,
  handleEditGoal,
  handleDetailsGoal,
  handleDeleteGoal,
  emptyStateComponent,
  onItemClick,
  targetLink,
  stickyHeader = false,
}: GoalsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renderPeriod, renderAssignToAvatar, getOwnerName } = useGoalsHook();
  const { renderTimeAgoShort, renderEmployeeName } = useFormatter();

  const renderBaseInfo = (goal: ApiGoal) => {
    return (
      <Stack direction={"horizontal"} gap={"smd"} items={"center"}>
        {renderAssignToAvatar(goal)}
        <TruncatedGoalTitle title={goal.title} />
      </Stack>
    );
  };

  const columns = useMemo<ColumnDef<FlatGoalRow>[]>(
    () => [
      {
        id: "goals",
        header: t("common:title"),
        accessorKey: "goal.title",
        cell: (cell) => {
          const { goal, depth, path } = cell.row.original;
          const isPathLoading = loadingPaths.has(path);
          const isExpanded = expandedPaths.has(path);

          return (
            <Stack
              direction={"horizontal"}
              gap={"smd"}
              items={"center"}
              style={{ marginLeft: `${depth * 2}rem` }}
              className={"pr-2"}
            >
              {goal.has_children ? (
                <Button
                  variant={"plain"}
                  className={"shrink-0"}
                  size={"sm"}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleExpand(goal.id, path);
                  }}
                  disabled={isPathLoading}
                >
                  {isPathLoading ? (
                    <FontAwesomeIcon icon="fa-light fa-spinner" className="animate-spin" />
                  ) : (
                    <FontAwesomeIcon
                      icon={isExpanded ? "fa-light fa-chevron-down" : "fa-light fa-chevron-right"}
                    />
                  )}
                </Button>
              ) : (
                <Box className="w-6 shrink-0" />
              )}
              {onItemClick ? (
                <Box className="cursor-pointer" onClick={() => onItemClick(goal)}>
                  {renderBaseInfo(goal)}
                </Box>
              ) : (
                <LinkTo to={`/goals/${goal.id}`} className={"no-underline"} target={targetLink}>
                  {renderBaseInfo(goal)}
                </LinkTo>
              )}
            </Stack>
          );
        },
        ...ColumnSizes.employee,
        meta: { disableRowClick: true },
      },
      {
        id: "responsible",
        header: t("common:assign_to"),
        cell: (cell) => {
          const { goal } = cell.row.original;
          if (!goal.assign_to) return "-";

          const ownerName = getOwnerName(goal);

          return (
            <Stack
              gap={"sm"}
              direction={"horizontal"}
              items={"center"}
              className={"whitespace-nowrap"}
            >
              <Text className="text-dark/95">{renderEmployeeName(goal.assign_to)}</Text>
              {ownerName && <Text className="text-dark/40">@{ownerName}</Text>}
            </Stack>
          );
        },
        ...ColumnSizes.employee,
      },
      {
        id: "period",
        header: t("common:period"),
        cell: (cell) => {
          const { goal } = cell.row.original;
          return (
            <Text className={"pr-2 whitespace-nowrap"}>
              {renderPeriod(goal.date_start, goal.date_end)}
            </Text>
          );
        },
        ...ColumnSizes.status,
      },
      {
        id: "last_update",
        header: t("goals:last_update"),
        cell: (cell) => {
          const { goal } = cell.row.original;
          if (!goal.datetime_key_results_update_at) return "-";
          return <Text>{renderTimeAgoShort(new Date(goal.datetime_key_results_update_at))}</Text>;
        },
        ...ColumnSizes.status,
      },
      {
        id: "progress",
        header: t("common:progress"),
        cell: (cell) => {
          const { goal } = cell.row.original;
          return (
            <Stack gap={"md"} direction={"horizontal"} items={"center"}>
              <Box className={"h-1 bg-dark/10 rounded-xs relative w-36 overflow-hidden"}>
                <Box
                  className={`h-full absolute left-0 top-0 rounded-xs ${goal.progress === 1 ? "bg-success" : "bg-dark"}`}
                  style={{
                    width: `${goal.progress ? goal.progress * 100 : 0}%`,
                  }}
                ></Box>
              </Box>
              {goal.goal_key_results_count > 0 ? (
                <Tooltip>
                  <TooltipTrigger>
                    <Stack
                      className="h-4 min-w-4 p-x-1 rounded-full bg-dark/60 text-sm text-light font-semibold shrink-0"
                      items={"center"}
                      justify={"center"}
                    >
                      {goal.goal_key_results_count}
                    </Stack>
                  </TooltipTrigger>
                  <TooltipContent>
                    <Text>{t("goals:count_key_results_in_goal")}</Text>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
        ...ColumnSizes.standard,
      },
    ],
    [expandedPaths, loadingPaths, toggleExpand, onItemClick, targetLink]
  );

  const renderRowAction = useCallback(
    (row: Row<FlatGoalRow>) => {
      const { goal } = row.original;
      const { can_edit, can_delete } = goal.acl ?? {};

      if (!can_edit && !can_delete) {
        return null;
      }

      return (
        <DropdownMenu>
          {can_edit && (
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />}
              onClick={() => handleEditGoal(goal)}
              title={t("common:edit")}
            />
          )}
          <DropdownMenuItem
            leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-info" />}
            onClick={() => handleDetailsGoal(goal)}
            title={t("common:details")}
          />
          {can_delete && (
            <DropdownMenuItem
              variant={"menuDanger"}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
              onClick={() => handleDeleteGoal(goal)}
              title={t("common:delete") + "..."}
            />
          )}
        </DropdownMenu>
      );
    },
    [handleEditGoal, handleDetailsGoal, handleDeleteGoal, t]
  );

  const defaultEmptyState = (
    <EmptyStateBase
      emoji={":dart:"}
      title={t("goals:goals_empty_state_title")}
      description={t("goals:goals_empty_state_description")}
    />
  );

  return (
    <Table
      stickyHeader={stickyHeader}
      renderRowAction={renderRowAction}
      onRowClick={(row) =>
        onItemClick ? onItemClick(row.original.goal) : navigate(`/goals/${row.original.goal.id}`)
      }
      data={flatGoals}
      columns={columns}
      showSkeleton={isLoading}
      emptyStateComponent={emptyStateComponent ?? defaultEmptyState}
    />
  );
}
